import {
  STATE_LOADING,
  STATE_ERROR,
  STATE_LOADED,
  stateType,
} from './../state';
import {
  flow,
  type Instance,
  types,
  type IAnyModelType,
  cast,
} from 'mobx-state-tree';
import { set } from 'mobx';
import type { Language } from '@seek/melways-sites';
import Job, { type IJob } from './Job';
import { logError } from '../../utils/logError';
import { AccessType } from './types';
import QuestionnaireStore from './QuestionnaireStore';
import {
  PostChannel,
  type AccessAndJobQuery,
  type ListingIdResponseSuccess,
  type PositionIdResponseSuccess,
} from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import fetchAccessAndJob from 'src/graphql/hirer/fetchAccessAndJob';

interface GraphLocation {
  id: string;
  parent?: GraphLocation | null;
}

const getFlattenedLocationHierarchy = (
  location: GraphLocation | null | undefined,
): string[] => {
  if (!location) {
    return [];
  }

  return [
    location.id,
    ...(location.parent ? getFlattenedLocationHierarchy(location.parent) : []),
  ];
};

const extractQuestionnaireId = (questionnaireUrl: string): string => {
  const result = questionnaireUrl.split('questionnaires/');
  return result.length === 2 ? result[1] : '';
};

const JobStore = types
  .model('JobStore', {
    job: types.optional(Job, {}),
    state: stateType,
    accessType: types.optional(
      types.enumeration<AccessType>('AccessType', Object.values(AccessType)),
      AccessType.AUTHORISED,
    ),
    questionnaire: types.optional(
      types.late((): IAnyModelType => QuestionnaireStore),
      {
        questionnaire: {
          state: STATE_LOADING,
        },
      },
    ),
  })
  .views((self) => ({
    get isError() {
      return self.state === STATE_ERROR;
    },

    get isLoading() {
      return self.state === STATE_LOADING;
    },

    get hasAccess(): boolean {
      return self.accessType === AccessType.AUTHORISED;
    },
  }))
  .actions((self) => {
    const loadHirerJobDetailsAndAccess = flow(function* (
      jobIdType: 'positionId' | 'jobId',
      expectedJobId: string,
      language: Language,
    ) {
      try {
        const { job: fetchAccessAndJobResult }: AccessAndJobQuery =
          jobIdType === 'positionId'
            ? yield fetchAccessAndJob({
                input: {
                  positionId: expectedJobId,
                  jobId: '',
                  language,
                },
              })
            : yield fetchAccessAndJob({
                input: {
                  positionId: 0,
                  jobId: `${expectedJobId}`,
                  language,
                },
              });

        self.accessType =
          fetchAccessAndJobResult.__typename === 'JobResponseSuccess'
            ? AccessType.AUTHORISED
            : AccessType.UNAUTHORISED;

        if (
          fetchAccessAndJobResult.__typename ===
          'CorrectUserIncorrectHirerResponseSuccess'
        ) {
          self.accessType = AccessType.INCORRECT_ADVERTISER;
        }

        if (self.accessType !== AccessType.AUTHORISED) {
          self.state = STATE_ERROR;
          return;
        }

        if (fetchAccessAndJobResult.__typename === 'JobResponseSuccess') {
          const jobInfo = fetchAccessAndJobResult.result;
          if (jobInfo.positionId.__typename === 'ResponseError') {
            logError(
              {
                errorCode: 'INVALID_POSITION_LISTING_ID',
                description:
                  'Invalid positionId or ListingId upon return from checkAccessAndFetchJobInformation',
              },
              {
                jobId: jobInfo.id,
              },
            );
            self.state = STATE_ERROR;
            return;
          }

          if (jobInfo.listingId.__typename === 'ResponseError') {
            logError(
              {
                errorCode: 'INVALID_POSITION_LISTING_ID',
                description:
                  'Invalid listingId or ListingId upon return from checkAccessAndFetchJobInformation',
              },
              {
                jobId: jobInfo.id,
              },
            );
            self.state = STATE_ERROR;
            return;
          }

          self.job.jobInformation.jobId = parseInt(jobInfo.id, 10);
          self.job.jobInformation.expiredDate = jobInfo.expiryDate ?? null;
          self.job.jobInformation.listingId = parseInt(
            (jobInfo.listingId as ListingIdResponseSuccess).result,
            10,
          );
          self.job.positionId = parseInt(
            (jobInfo.positionId as PositionIdResponseSuccess).result,
            10,
          );
          self.job.jobInformation.positionId = self.job.positionId;
          self.job.jobInformation.listingStartedDate = String(
            jobInfo.listingDate,
          );
          self.job.jobInformation.numberOfDaysRemaining =
            jobInfo.daysRemaining ?? 0;
          self.job.jobInformation.positionCreatedDate = jobInfo.createdDate;
          self.job.jobInformation.postedBy = jobInfo.owner?.fullName ?? '';
          self.job.jobInformation.questionnaireId = extractQuestionnaireId(
            jobInfo.questionnaireUrl ?? '',
          );

          // job details
          self.job.jobInformation.analyticsAdRatingStatus =
            jobInfo.performanceRating?.status ?? null;
          self.job.jobInformation.setPostChannel(
            jobInfo.postChannel || PostChannel.DIRECT,
          );
          self.job.jobInformation.status = jobInfo.status;
          self.job.jobInformation.adType = jobInfo.adType ?? null;
          self.job.jobInformation.productDisplayName =
            jobInfo.productDisplayName || null;
          self.job.jobInformation.title = jobInfo.title;
          self.job.jobInformation.workType = jobInfo.workType ?? null;
          self.job.jobInformation.locations = cast(
            jobInfo.locations.map((location) => location.description),
          );
          self.job.jobInformation.setJobCountry(jobInfo.locations);
          self.job.jobInformation.classification = jobInfo.categoryName ?? null;
          self.job.jobInformation.categoryId = jobInfo.categoryId ?? null;
          self.job.jobInformation.jobCreatedDate = jobInfo.createdDate ?? null;
          self.job.jobInformation.subClassification =
            jobInfo.subCategoryName ?? null;
          self.job.jobInformation.internalReference =
            jobInfo.advertiserJobReference ?? undefined;

          self.job.jobInformation.salary = jobInfo.salaryText ?? null;
          self.job.jobInformation.salaryMin = jobInfo.minSalary ?? null;
          self.job.jobInformation.salaryMax = jobInfo.maxSalary ?? null;
          self.job.jobInformation.salaryCurrency =
            jobInfo.salaryCurrency ?? null;
          self.job.jobInformation.setSalaryType(jobInfo.payType || null);

          if (
            jobInfo.billableApplicants.__typename ===
              'BillableApplicantsResponseSuccess' &&
            jobInfo.billableApplicants.data?.billableApplicantsCount
              .__typename === 'BillableApplicantsCountResponseSuccess' &&
            jobInfo.billableApplicants.data?.maxBillableApplicants
              .__typename === 'MaxBillableApplicantsResponseSuccess'
          ) {
            self.job.jobInformation.billableApplicants = jobInfo
              .billableApplicants.data
              ? {
                  success: true,
                  billableApplicantsCount:
                    jobInfo.billableApplicants.data.billableApplicantsCount
                      .result,
                  maxBillableApplicants:
                    jobInfo.billableApplicants.data.maxBillableApplicants
                      .result,
                }
              : undefined;
          } else if (
            jobInfo.billableApplicants.__typename === 'ResponseError' ||
            (jobInfo.billableApplicants.__typename ===
              'BillableApplicantsResponseSuccess' &&
              (jobInfo.billableApplicants.data?.billableApplicantsCount
                .__typename === 'ResponseError' ||
                jobInfo.billableApplicants.data?.maxBillableApplicants
                  .__typename === 'ResponseError'))
          ) {
            self.job.jobInformation.billableApplicants = {
              success: false,
            };
          }

          const locationIds: string[] = [];
          jobInfo.locations.forEach((location) =>
            locationIds.push(...getFlattenedLocationHierarchy(location)),
          );

          self.job.jobInformation.locationIds = cast(locationIds);
          self.state = STATE_LOADED;
          return jobInfo;
        }
      } catch (error: any) {
        logError(
          {
            errorCode: 'GET_JOB',
            description: 'Error attempting: fetchAccessAndJob',
          },
          { error, jobIdType, expectedJobId, language },
        );
        self.state = STATE_ERROR;
      }
    });

    const setAccessType = (accessType?: AccessType) => {
      if (accessType) {
        self.accessType = accessType;
      }
    };

    const setJob = (job: IJob) => {
      set(self.job, job);
      self.state = STATE_LOADED;
    };

    const setIsError = () => {
      self.state = STATE_ERROR;
    };

    return {
      loadHirerJobDetailsAndAccess,
      setAccessType,
      setJob,
      setIsError,
    };
  });

export interface IJobStore extends Instance<typeof JobStore> {}

export default JobStore;
